import React from "react";
import {
  Shield,
  Clock,
  Mail,
  Download,
  Trash2,
  AlertTriangle,
  BarChart3,
  ChevronRight,
  Eye,
  ExternalLink,
  Settings,
  ArrowLeft,
  Calendar,
  FileText,
  HelpCircle,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Feature } from "@core/components/Feature";
import { useToast } from "@core/hooks/use-toast";
import { logError } from "@core/lib/error-handling";
import { useDataControl } from "@core/hooks/useDataControl";
import { type ProductType } from "@core/config/products.config";
import DeleteAccountModal from "@core/components/account/DeleteAccountModal";

interface DataControlUIProps {
  productType: ProductType;
  settings: any;
  filteredData: any;
  upcomingDeletions: any[];
  showPreview: boolean;
  showExportModal: boolean;
  showDeleteModal?: boolean;
  exportFormat: "json" | "csv" | "pdf" | "markdown";
  hasEmailAccount: boolean;
  config: any;
  setSettings: (settings: any) => void;
  setShowPreview: (show: boolean) => void;
  setShowExportModal: (show: boolean) => void;
  setShowDeleteModal?: (show: boolean) => void;
  setExportFormat: (format: "json" | "csv" | "pdf" | "markdown") => void;
  handleSettingChange: (key: string, value: string | boolean) => void;
  handleExportData: (format: "json" | "csv" | "pdf" | "markdown") => void;
  handleDeleteAccount: () => void;
  postponeDeletion: (type: string) => void;
  copyDpoEmail: () => void;
}

export const DataControlUI: React.FC<DataControlUIProps> = ({
  productType,
  settings,
  filteredData,
  upcomingDeletions,
  showPreview,
  showExportModal,
  showDeleteModal = false,
  exportFormat,
  hasEmailAccount,
  config,
  setSettings,
  setShowPreview,
  setShowExportModal,
  setShowDeleteModal,
  setExportFormat,
  handleSettingChange,
  handleExportData,
  handleDeleteAccount,
  postponeDeletion,
  copyDpoEmail,
}) => {
  const { toast } = useToast();
  const { t, i18n } = useTranslation("polls");

  // Détecter le produit d'origine pour le retour
  const getOriginProduct = () => {
    const referrer = document.referrer;
    const currentPath = window.location.pathname;

    // Détecter depuis le referrer ou le path courant
    if (
      referrer.includes("localhost:3005") ||
      currentPath.startsWith("/date")
    ) {
      return { name: "Date Polls", path: "/date", icon: Calendar };
    }
    if (
      referrer.includes("localhost:3002") ||
      currentPath.startsWith("/form")
    ) {
      return { name: "Form Polls", path: "/form", icon: FileText };
    }
    if (
      referrer.includes("localhost:3006") ||
      currentPath.startsWith("/quizz")
    ) {
      return { name: "Quizz", path: "/quizz", icon: HelpCircle };
    }
    if (
      referrer.includes("localhost:3003") ||
      currentPath.startsWith("/availability")
    ) {
      return { name: "Availability", path: "/availability", icon: Users };
    }

    return { name: "Accueil", path: "/", icon: ArrowLeft };
  };

  const originProduct = getOriginProduct();
  const privacyPath =
    originProduct.path === "/" ? "/privacy" : `${originProduct.path}/privacy`;

  return (
    <div className="min-h-screen-safe bg-gray-900">
      <div className="pt-4">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Header puissant */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-6">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">
              {t("dataControl.title")}
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              {t("dataControl.subtitle")}
              <span className="font-semibold text-blue-400">
                {" "}
                {t("dataControl.subtitleHighlight")}
              </span>
            </p>
          </div>

          {/* Alertes suppressions à venir */}
          {upcomingDeletions.length > 0 && (
            <div className="mb-8 space-y-4">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-orange-400" />
                {t("dataControl.upcomingDeletions.heading")}
              </h2>
              {upcomingDeletions.map((warning, index) => (
                <div
                  key={index}
                  className="bg-orange-900/30 border border-orange-700/50 rounded-lg p-6"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Clock className="w-5 h-5 text-orange-400" />
                        <h3 className="font-semibold text-orange-200">
                          {warning.type === "chat"
                            ? filteredData.labels.conversationName
                            : filteredData.labels.itemPlural}
                        </h3>
                        <span className="px-2 py-1 bg-orange-800/50 text-orange-200 text-xs font-medium rounded">
                          {t("dataControl.upcomingDeletions.daysBadge", {
                            count: warning.daysUntilDeletion,
                          })}
                        </span>
                      </div>
                      <p className="text-orange-200 mb-3">
                        {warning.itemCount}{" "}
                        {warning.type === "chat"
                          ? filteredData.labels.conversationName
                          : filteredData.labels.itemPlural}{" "}
                        {t("dataControl.upcomingDeletions.willBeDeletedOn", {
                          date: warning.deletionDate.toLocaleDateString(
                            i18n.language || "fr",
                          ),
                        })}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-orange-300">
                        <span>
                          {hasEmailAccount
                            ? t(
                                "dataControl.upcomingDeletions.emailAlertPlanned",
                              )
                            : t("dataControl.upcomingDeletions.noEmailAlert")}
                        </span>
                        <button
                          onClick={() => postponeDeletion(warning.type)}
                          className="text-orange-300 hover:text-orange-100 font-medium underline"
                          data-testid="data-control-postpone"
                        >
                          {t("dataControl.upcomingDeletions.postpone")}
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowPreview(true)}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-500 transition-colors text-sm font-medium"
                      data-testid="data-control-preview"
                    >
                      {t("dataControl.upcomingDeletions.viewData")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Contrôle des données */}
          <div className="bg-gray-800 rounded-xl shadow-sm border border-gray-700 mb-8">
            <div className="p-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <Settings className="w-6 h-6 text-blue-400" />
                {t("dataControl.settings.heading")}
              </h2>

              <div className="space-y-8">
                {/* Conservation conversations IA */}
                <div>
                  <label className="block text-lg font-semibold text-white mb-3">
                    {filteredData.labels.conversationPlural}
                  </label>
                  <select
                    value={settings.chatRetention}
                    onChange={(e) =>
                      handleSettingChange("chatRetention", e.target.value)
                    }
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                  >
                    <option value="30-days">
                      {t("dataControl.chatRetention.option30Days")}
                    </option>
                    <option value="12-months">
                      {t("dataControl.chatRetention.option12Months")}
                    </option>
                  </select>
                  <p className="text-sm text-gray-400 mt-2">
                    {t("dataControl.chatRetention.hint")}
                  </p>
                </div>

                {/* Conservation sondages */}
                <div>
                  <label className="block text-lg font-semibold text-white mb-3">
                    {filteredData.labels.itemPlural}
                  </label>
                  <select
                    value={settings.pollRetention}
                    onChange={(e) =>
                      handleSettingChange("pollRetention", e.target.value)
                    }
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                  >
                    <option value="unlimited">
                      {t("dataControl.pollRetention.optionUnlimited")}
                    </option>
                    <option value="12-months">
                      {t("dataControl.pollRetention.option12Months")}
                    </option>
                    <option value="24-months">
                      {t("dataControl.pollRetention.option24Months")}
                    </option>
                  </select>
                  <p className="text-sm text-gray-400 mt-2">
                    {t("dataControl.pollRetention.hint")}
                  </p>
                </div>

                {/* Suppression automatique */}
                <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-semibold text-white mb-1">
                      {t("dataControl.autoDelete.title")}
                    </h3>
                    <p className="text-gray-300">
                      {t("dataControl.autoDelete.description")}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.autoDeleteEnabled}
                      onChange={(e) =>
                        handleSettingChange(
                          "autoDeleteEnabled",
                          e.target.checked,
                        )
                      }
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-7 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {/* Amélioration produit */}
                <div className="flex items-center justify-between p-4 bg-blue-900/30 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-semibold text-blue-300 mb-1">
                      {t("dataControl.improvement.title")}
                    </h3>
                    <p className="text-blue-200">
                      {t("dataControl.improvement.description")}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.allowDataForImprovement}
                      onChange={(e) =>
                        handleSettingChange(
                          "allowDataForImprovement",
                          e.target.checked,
                        )
                      }
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-7 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {/* Notifications email */}
                <div className="flex items-center justify-between p-4 bg-green-900/30 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-semibold text-green-300 mb-1 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {t("dataControl.emailAlerts.title")}
                    </h3>
                    <p className="text-green-200">
                      {t("dataControl.emailAlerts.description")}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.emailNotifications}
                      onChange={(e) =>
                        handleSettingChange(
                          "emailNotifications",
                          e.target.checked,
                        )
                      }
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-7 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-600"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Actions rapides */}
          <div className="bg-gray-800 rounded-xl shadow-sm border border-gray-700 mb-8">
            <div className="p-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <BarChart3 className="w-6 h-6 text-green-400" />
                {t("dataControl.quickActions.heading")}
              </h2>

              <div className="grid md:grid-cols-2 gap-4">
                <button
                  onClick={() => setShowExportModal(true)}
                  className="flex items-center justify-between p-6 bg-green-900/30 rounded-lg hover:bg-green-900/50 transition-colors group"
                  data-testid="export-data-button"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-800/50 rounded-full flex items-center justify-center">
                      <Download className="w-6 h-6 text-green-400" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-white group-hover:text-green-300">
                        {t("dataControl.quickActions.export.title")}
                      </p>
                      <p className="text-sm text-gray-300">
                        {t("dataControl.quickActions.export.description")}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-green-400" />
                </button>

                <button
                  onClick={handleDeleteAccount}
                  className="flex items-center justify-between p-6 bg-red-900/30 rounded-lg hover:bg-red-900/50 transition-colors group"
                  data-testid="delete-account-button"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-red-800/50 rounded-full flex items-center justify-center">
                      <Trash2 className="w-6 h-6 text-red-400" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-red-300 group-hover:text-red-200">
                        {t("dataControl.quickActions.delete.title")}
                      </p>
                      <p className="text-sm text-red-200">
                        {t("dataControl.quickActions.delete.description")}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-red-500 group-hover:text-red-400" />
                </button>
              </div>
            </div>
          </div>

          {/* Documentation et support */}
          <div className="bg-gradient-to-r from-blue-900/30 to-indigo-900/30 rounded-xl p-8 border border-blue-700/50">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <Eye className="w-6 h-6 text-blue-400" />
              {t("dataControl.transparency.heading")}
            </h2>

            <div className="grid md:grid-cols-1 gap-4">
              <Link
                to={privacyPath}
                className="flex items-center justify-between p-4 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-blue-400" />
                  <div className="text-left">
                    <p className="font-medium text-white">
                      {t("dataControl.transparency.privacyPolicy.title")}
                    </p>
                    <p className="text-sm text-gray-300">
                      {t("dataControl.transparency.privacyPolicy.description")}
                    </p>
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 text-gray-500" />
              </Link>
            </div>

            {/* Contact DPO */}
            <div className="mt-6 p-4 bg-gray-800 rounded-lg border border-blue-700/50">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="font-medium text-blue-300">
                    {t("dataControl.dpo.title")}
                  </p>
                  <p className="text-sm text-blue-200">
                    {t("dataControl.dpo.question")}{" "}
                    <button
                      onClick={copyDpoEmail}
                      className="underline hover:text-white"
                    >
                      privacy@doodates.com
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-white mb-4">
              {t("dataControl.exportModal.title")}
            </h3>
            <div className="space-y-3">
              <button
                onClick={() => handleExportData("json")}
                className="w-full p-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-left"
              >
                <div className="font-medium">JSON</div>
                <div className="text-sm text-gray-300">
                  {t("dataControl.exportModal.json")}
                </div>
              </button>
              <button
                onClick={() => handleExportData("csv")}
                className="w-full p-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-left"
              >
                <div className="font-medium">CSV</div>
                <div className="text-sm text-gray-300">
                  {t("dataControl.exportModal.csv")}
                </div>
              </button>
              <button
                onClick={() => handleExportData("pdf")}
                className="w-full p-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-left"
              >
                <div className="font-medium">PDF</div>
                <div className="text-sm text-gray-300">
                  {t("dataControl.exportModal.pdf")}
                </div>
              </button>
              <button
                onClick={() => handleExportData("markdown")}
                className="w-full p-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-left"
              >
                <div className="font-medium">Markdown</div>
                <div className="text-sm text-gray-300">
                  {t("dataControl.exportModal.markdown")}
                </div>
              </button>
            </div>
            <button
              onClick={() => setShowExportModal(false)}
              className="mt-4 w-full p-3 bg-gray-600 hover:bg-gray-500 rounded-lg text-white"
            >
              {t("dataControl.exportModal.cancel")}
            </button>
          </div>
        </div>
      )}

      {/* Modal de suppression de compte (double confirmation) */}
      {setShowDeleteModal && (
        <DeleteAccountModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default DataControlUI;
